import type { VoiceTypeKey } from '@/src/dal/user-dal'
import { useMemo, useReducer } from 'react'
import { useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useRouter } from 'expo-router'
import { getVoiceAssessmentSessionDefinitionId } from '@/src/config/voice-assessment-session'
import { useAppSession } from '@/src/contexts/AppSessionContext'
import { getProgressTotal } from '@/src/features/voice-assessment/components/VoiceAssessmentFlow.shared'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { useUpdateUserMutation } from '@/src/hooks/useUpdateUserMutation'
import { useSetExerciseGoal } from '@/src/queries/exercise-goal-queries'
import { sessionDefinitionQueryOptions } from '@/src/queries/session-queries'
import { appQueryOptions } from '@/src/queries/view-queries'
import {
  buildOnboardingCompletedProperties,
  MixpanelEvents,
  trackEvent,
} from '@/src/services/analytics'
import { showAlert } from '@/src/utils/alert'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { KnowledgeLevelOptionKey, PostPaymentOption } from './types'
import { useCompletePostPaymentOnboarding } from '../../hooks/useCompletePostPaymentOnboarding'
import { WAVE_ASPECT_RATIO } from './constants'
import { INITIAL_STATE, postPaymentOnboardingReducer } from './state'
import { getVoiceTypeOptions } from './voice-type-options'

const POST_PAYMENT_STEPS_BEFORE_ASSESSMENT_FLOW = 3

export function usePostPaymentOnboardingScreen() {
  const { t } = useTranslation(['common', 'onboarding', 'profile'])
  const router = useRouter()
  const queryClient = useQueryClient()
  const insets = useSafeAreaInsets()
  const { width: windowWidth } = useWindowDimensions()
  const { backendUser, isLoading, logout } = useAppSession()
  const completePostPaymentOnboardingMutation =
    useCompletePostPaymentOnboarding()
  const updateUserMutation = useUpdateUserMutation()
  const [state, dispatch] = useReducer(
    postPaymentOnboardingReducer,
    INITIAL_STATE,
  )
  const contentfulLanguage = useContentfulLocale()

  const handleSignOut = async () => {
    if (state.isSigningOut) return
    dispatch({ type: 'setIsSigningOut', payload: true })

    const didSignOut = await logout()
      .then(() => true)
      .catch((error) => {
        console.error('Sign out failed', error)
        showAlert(t('common:error'))
        return false
      })

    if (!didSignOut) {
      dispatch({ type: 'setIsSigningOut', payload: false })
    }
  }

  const voiceTypeOptions = useMemo<PostPaymentOption<VoiceTypeKey>[]>(
    () => getVoiceTypeOptions(t),
    [t],
  )

  const knowledgeLevelOptions = useMemo<
    PostPaymentOption<KnowledgeLevelOptionKey>[]
  >(
    () => [
      {
        key: 'beginner',
        label: t('onboarding:post_payment.knowledge_levels.beginner'),
      },
      {
        key: 'intermediate',
        label: t('onboarding:post_payment.knowledge_levels.intermediate'),
      },
      {
        key: 'advanced',
        label: t('onboarding:post_payment.knowledge_levels.advanced'),
      },
    ],
    [t],
  )

  const waveWidth = windowWidth
  const waveHeight = WAVE_ASPECT_RATIO * waveWidth
  const detailsFooterPaddingBottom = Math.max(insets.bottom, 24)
  const pageTitle = t('common:page_title_onboarding')
  const logoutLabel = t('profile:logout')
  const stepOneTitle = t('onboarding:post_payment.step_1_title')
  const stepOneDescription = t('onboarding:post_payment.step_1_description')
  const voiceTypeLabel = t('onboarding:post_payment.voice_type')
  const exerciseFrequencyLabel = t('onboarding:post_payment.exercise_frequency')
  const knowledgeLevelLabel = t('onboarding:post_payment.knowledge_level')
  const notesLabel = t('onboarding:post_payment.notes_label')
  const notesPlaceholder = t('onboarding:post_payment.notes_placeholder')
  const continueLabel = t('onboarding:post_payment.step_1_cta')
  const nextLabel = t('onboarding:post_payment.step_2_cta_next')
  const loadingLabel = t('common:loading')
  const submitLabel = t('onboarding:post_payment.step_2_cta')

  const setExerciseGoalMutation = useSetExerciseGoal()
  const appQuery = useQuery(appQueryOptions(contentfulLanguage))
  const selectedSessionDefinitionId = useMemo(() => {
    if (appQuery.data == null) {
      return null
    }

    return getVoiceAssessmentSessionDefinitionId({
      app: appQuery.data,
      user: {
        voiceType:
          state.selectedVoiceType ?? backendUser?.voiceType ?? 'unknown',
      },
    })
  }, [appQuery.data, backendUser?.voiceType, state.selectedVoiceType])
  const selectedSessionDefinitionQuery = useQuery({
    ...sessionDefinitionQueryOptions(
      selectedSessionDefinitionId ?? 0,
      contentfulLanguage,
    ),
    enabled: selectedSessionDefinitionId != null,
  })
  const postPaymentProgressTotal =
    selectedSessionDefinitionQuery.data == null
      ? null
      : POST_PAYMENT_STEPS_BEFORE_ASSESSMENT_FLOW +
        getProgressTotal(
          selectedSessionDefinitionQuery.data.measurementDefinitions,
        )

  const isSubmitting =
    setExerciseGoalMutation.isPending ||
    completePostPaymentOnboardingMutation.isPending ||
    updateUserMutation.isPending

  const canContinueDetails = state.selectedVoiceType != null

  const handleComplete = async () => {
    if (isSubmitting || state.selectedVoiceType == null) return

    try {
      const goalMutationResult = await setExerciseGoalMutation
        .mutateAsync(state.selectedDaysPerWeek)
        .then(
          () => ({ status: 'fulfilled' as const }),
          () => ({ status: 'rejected' as const }),
        )
      await updateUserMutation.mutateAsync({
        email: backendUser?.email ?? null,
        firstName: backendUser?.firstName ?? null,
        voiceType: state.selectedVoiceType,
      })
      if (goalMutationResult.status === 'rejected') {
        console.warn('Exercise goal update failed during onboarding completion')
      }
      trackEvent(
        MixpanelEvents.OnboardingCompleted,
        buildOnboardingCompletedProperties({
          voiceType: state.selectedVoiceType,
          experienceLevel: state.selectedKnowledgeLevel,
        }),
      )

      void queryClient
        .ensureQueryData(appQueryOptions(contentfulLanguage))
        .then((app) =>
          queryClient.prefetchQuery(
            sessionDefinitionQueryOptions(
              getVoiceAssessmentSessionDefinitionId({
                app,
                user:
                  backendUser == null
                    ? { voiceType: state.selectedVoiceType }
                    : {
                        ...backendUser,
                        voiceType: state.selectedVoiceType,
                      },
              }),
              contentfulLanguage,
            ),
          ),
        )
        .catch(() => undefined)
      router.replace({
        pathname: '/voice-assessment',
        params: { source: 'onboarding' },
      })
      await completePostPaymentOnboardingMutation.mutateAsync()
    } catch (error) {
      console.error('Failed to complete post-payment onboarding', error)
      showAlert(t('common:error'))
    }
  }

  return {
    state,
    dispatch,
    isLoading,
    isSubmitting,
    canContinueDetails,
    voiceTypeOptions,
    knowledgeLevelOptions,
    waveWidth,
    waveHeight,
    detailsFooterPaddingBottom,
    pageTitle,
    logoutLabel,
    stepOneTitle,
    stepOneDescription,
    voiceTypeLabel,
    exerciseFrequencyLabel,
    knowledgeLevelLabel,
    notesLabel,
    notesPlaceholder,
    continueLabel,
    nextLabel,
    loadingLabel,
    submitLabel,
    postPaymentProgressTotal,
    insetsBottom: insets.bottom,
    handleSignOut,
    handleComplete,
  }
}
